import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { SocialAuthButtons } from '../components/SocialAuthButtons';
import { TurnstileWidget } from '../components/TurnstileWidget';
import { WhatsAppSenderHint } from '../components/WhatsAppSenderHint';
import { PhoneInput } from '../components/PhoneInput';
import { isValidE164 } from '../lib/validatePhone';

type IdentifierMode = 'phone' | 'email';

// Unified passwordless sign-in / sign-up page.
//
// Step 1: user enters a phone number OR email (format auto-detected).
// Step 2: we send a 6-digit OTP — via WhatsApp for phones, SES for email —
//         through Cognito's Custom Auth flow.
// Step 3: user enters the code; Cognito issues tokens AND our backend
//         auto-creates the primary verified Channel row. The same code
//         proves auth and channel ownership in one step.
//
// /signin and /signup both render this page; the distinction between
// "existing user" and "new user" is transparent — sendCode creates the
// user on-the-fly if they don't exist yet.
export function AuthPage() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, isLoading, sendCode, confirmCode, signInWithGoogle, signInWithGitHub } = useAuth();

  const [step, setStep] = useState<'identifier' | 'code'>('identifier');
  const [mode, setMode] = useState<IdentifierMode>('phone');
  const [identifier, setIdentifier] = useState('');
  // Collected alongside a PHONE identifier so every account has a second
  // reachable destination (Meta silently caps WhatsApp per recipient).
  const [backupEmail, setBackupEmail] = useState('');
  const [code, setCode] = useState('');
  const [destination, setDestination] = useState<string | undefined>();
  const [channel, setChannel] = useState<'whatsapp' | 'email' | undefined>();
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [existingAccountMethods, setExistingAccountMethods] = useState<string[] | null>(null);
  const turnstileConfigured = !!import.meta.env.VITE_TURNSTILE_SITE_KEY;
  const handleTurnstileToken = useCallback((t: string | null) => setTurnstileToken(t), []);

  // Pre-fill the identifier (and the mode toggle) from landing-page
  // intent. ?channel=email → email tab, otherwise default to phone.
  useEffect(() => {
    const prefilledPhone = searchParams.get('phone');
    const prefilledEmail = searchParams.get('email');
    const channelParam = searchParams.get('channel');
    // The landing form now always sends ?email=, so it can no longer be
    // used to infer the tab — ?channel= and the presence of ?phone= decide.
    // Checking email first would force every WhatsApp signup into email mode.
    if (channelParam === 'whatsapp' || (prefilledPhone && channelParam !== 'email')) {
      setMode('phone');
      if (prefilledPhone) setIdentifier(prefilledPhone);
      if (prefilledEmail) setBackupEmail(prefilledEmail);
    } else if (prefilledEmail || channelParam === 'email') {
      setMode('email');
      if (prefilledEmail) setIdentifier(prefilledEmail);
    }
  }, [searchParams]);

  // Already signed in → go to dashboard.
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  // Did the user arrive here from the landing page Hero form
  // ("Start getting alerts" — X handle + phone/email + channel)?
  // The X handle is the unambiguous signal: only LandingPage.handleStart
  // sets `?x=…` on the URL. When true, we lock the AuthPage down to a
  // single path: no Phone/Email tab toggle, no Google/GitHub buttons,
  // no "Continue with provider" fallback. The user already committed
  // to a channel choice on the landing page; surfacing alternatives
  // here just adds confusion mid-flow. The full menu remains
  // available on /signin and /signup when reached via header buttons.
  const fromLandingFlow = !!searchParams.get('x');

  // Single source of truth for "fire the OTP and advance to the code
  // step". Both the manual button click and the landing-flow auto-send
  // call into this — keeps validation, fragmentation handling, and
  // step transitions identical regardless of how the send was kicked off.
  const sendCodeNow = useCallback(async () => {
    setError('');
    setExistingAccountMethods(null);
    // Catch nonsense phone numbers client-side before burning an OTP
    // and hitting the per-destination cooldown — libphonenumber-js
    // rejects too-short / country-impossible numbers. Email shape is
    // already validated by type="email".
    if (mode === 'phone' && !isValidE164(identifier)) {
      setError(t('signIn.invalidPhone', {
        defaultValue: 'Please enter a valid phone number.',
      }));
      return { ok: false as const };
    }
    if (mode === 'phone' && !backupEmail.includes('@')) {
      setError(t('signIn.emailRequired', {
        defaultValue: 'Please enter your email address.',
      }));
      return { ok: false as const };
    }
    setSubmitting(true);
    try {
      const clientMetadata = turnstileToken ? { turnstileToken } : undefined;
      const result = await sendCode(
        identifier,
        clientMetadata,
        mode === 'phone' ? backupEmail : undefined,
      );
      // Fragmentation guard: useAuth returns an existingAccount hint
      // when the email/phone is already registered under a different
      // auth method. Render the "use {method} instead" prompt and
      // stop before the OTP is dispatched.
      if ('existingAccount' in result && result.existingAccount) {
        setExistingAccountMethods(result.methods.filter((m) => m !== 'passwordless'));
        return { ok: false as const };
      }
      const ok = result as { destination?: string; channel?: 'whatsapp' | 'email' };
      setDestination(ok.destination);
      setChannel(ok.channel);
      setStep('code');
      return { ok: true as const };
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send code.');
      return { ok: false as const };
    } finally {
      setSubmitting(false);
    }
  }, [identifier, mode, backupEmail, sendCode, t, turnstileToken]);

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    await sendCodeNow();
  };

  // Auto-fire the OTP on transition from the landing page. The user
  // already committed to the identifier when they clicked "Start
  // getting alerts" — making them click "Send code" again is just a
  // redundant step. Fires exactly once per page lifetime; ref guard
  // survives StrictMode double-mount and any re-render churn from
  // Turnstile / route state.
  //
  // The ref is NEVER reset on failure. The earlier "let it retry on
  // failure" approach caused a reload-loop UX for any failure that
  // bubbled into a render before the user could react: the failed
  // send sets error → finally clears submitting → effect re-fires
  // (deps include submitting) → sendCodeNow clears the error and
  // tries again, repeating endlessly. Concrete trigger we hit in
  // prod was the per-destination rate-limit gate (`dest:<sha256>`)
  // surviving a previous account's deletion. Failed sends now
  // surface `error` to the manual form (landingAutoFlow flips to
  // false) and the user retries via the Send-Code button, which
  // never depended on this ref.
  const autoSentRef = useRef(false);
  useEffect(() => {
    if (autoSentRef.current) return;
    if (!fromLandingFlow) return;
    if (step !== 'identifier') return;
    if (submitting) return;
    if (!identifier.trim()) return;
    if (mode === 'phone' && !isValidE164(identifier)) return;
    if (mode === 'email' && !identifier.includes('@')) return;
    if (turnstileConfigured && !turnstileToken) return;
    autoSentRef.current = true;
    void sendCodeNow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identifier, mode, turnstileToken, step, submitting]);

  const handleConfirmCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await confirmCode(code);
      // checkAuth in useAuth triggers the auth state update → the
      // isAuthenticated effect above navigates.
    } catch (err) {
      // Tagged from useAuth.confirmCode when Amplify reports the next
      // step is CONTINUE_SIGN_IN_WITH_CUSTOM_CHALLENGE — the code was
      // wrong but the user still has retries left.
      if (err instanceof Error && err.name === 'IncorrectCodeError') {
        setError(t('code.incorrectCode', {
          defaultValue: 'Incorrect code. Please check the latest one and try again.',
        }));
        // Clear the input so the user can retype without backspacing.
        setCode('');
      } else if (err instanceof Error && err.name === 'NotAuthorizedException') {
        // Cognito raises this once DefineAuthChallenge sets
        // failAuthentication=true (after 3 wrong attempts). The user
        // must request a fresh code.
        setError(t('code.tooManyAttempts', {
          defaultValue: 'Too many incorrect attempts. Please request a new code.',
        }));
        setCode('');
      } else {
        setError(err instanceof Error ? err.message : 'Incorrect code.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const isEmailIdentifier = mode === 'email';

  const switchMode = (next: IdentifierMode) => {
    if (next === mode) return;
    setMode(next);
    setIdentifier('');
    setError('');
    setExistingAccountMethods(null);
  };

  // While the landing-flow auto-send is in flight, hide the identifier
  // form entirely. Showing the phone field + Send-code button between
  // the user's "Start getting alerts" click and the OTP arriving is
  // confusing — they think we're asking them to re-enter what they
  // already gave us. Switch to a clean spinner + "sending to <dest>"
  // affordance instead. We surface the form again only if the send
  // fails (error set) or if the backend reports a fragmented account
  // (existingAccountMethods) — both cases where the user MUST act.
  const landingAutoFlow =
    fromLandingFlow &&
    step === 'identifier' &&
    !error &&
    !existingAccountMethods;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
      <div className="w-full max-w-md rounded-xl border border-gray-200 bg-white p-8 shadow-sm">
        <div className="mb-6 text-center">
          <img src="/assets/logo/logo-full-transparent.png" alt="WallaWhats" className="mx-auto h-10" />
          <h1 className="mt-4 text-2xl font-bold tracking-tight text-gray-900">
            {landingAutoFlow
              ? t('signIn.sendingTitle', { defaultValue: 'Sending your code…' })
              : step === 'identifier'
              ? t('signIn.title', { defaultValue: 'Sign in to WallaWhats' })
              : t('code.title', { defaultValue: 'Enter your code' })}
          </h1>
          {step === 'identifier' && !landingAutoFlow && (
            <p className="mt-2 text-sm text-gray-500">
              {t('signIn.subtitle', { defaultValue: 'We\'ll send you a 6-digit code — no password needed.' })}
            </p>
          )}
          {landingAutoFlow && (
            <p className="mt-2 text-sm text-gray-500">
              {t('signIn.sendingTo', {
                defaultValue: "We're sending a 6-digit code to {{destination}} — don't close this page.",
                destination: identifier,
              })}
            </p>
          )}
          {step === 'code' && destination && (
            <p className="mt-2 text-sm text-gray-500">
              {t('code.sentTo', {
                defaultValue: 'Sent to {{destination}} via {{channel}}.',
                destination,
                channel: channel === 'whatsapp' ? 'WhatsApp' : 'email',
              })}
            </p>
          )}
        </div>

        {landingAutoFlow && (
          <div className="flex flex-col items-center justify-center py-8 gap-4">
            <div className="h-10 w-10 rounded-full border-4 border-primary-200 border-t-primary-600 animate-spin" />
            <p className="text-sm text-gray-500">
              {t('signIn.sendingHint', {
                defaultValue: 'This usually takes a few seconds.',
              })}
            </p>
          </div>
        )}

        {step === 'identifier' && searchParams.get('oauth') && (
          // Failure / timeout banner for the OAuth bounce paths in
          // useAuth's redirect_failure / safety-timer handlers.
          // Tells the user what just happened so the spinner-then-
          // signin transition isn't a silent rug-pull.
          <div
            role="alert"
            className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-900"
          >
            {searchParams.get('oauth') === 'timeout'
              ? t('signIn.oauthTimeout', {
                  defaultValue:
                    "We couldn't complete the social sign-in. Please try again — if it keeps happening, use the phone or email option below.",
                })
              : searchParams.get('oauth') === 'relink-success'
              ? t('signIn.oauthRelinkSuccess', {
                  defaultValue:
                    'We just connected your social sign-in to your existing account. Click your social button again to finish signing in.',
                })
              : t('signIn.oauthFailed', {
                  defaultValue:
                    'Social sign-in failed. Please try again or use the phone / email option below.',
                })}
          </div>
        )}

        {step === 'identifier' && !landingAutoFlow ? (
          <form onSubmit={handleSendCode} className="space-y-4">
            {!fromLandingFlow && (
              <div className="flex gap-2 rounded-lg bg-gray-100 p-1">
                <button
                  type="button"
                  onClick={() => switchMode('phone')}
                  className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${mode === 'phone' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                  aria-pressed={mode === 'phone'}
                >
                  {t('signIn.tabPhone', { defaultValue: 'Phone' })}
                </button>
                <button
                  type="button"
                  onClick={() => switchMode('email')}
                  className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${mode === 'email' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                  aria-pressed={mode === 'email'}
                >
                  {t('signIn.tabEmail', { defaultValue: 'Email' })}
                </button>
              </div>
            )}
            <div>
              <label htmlFor="identifier" className="block text-sm font-medium text-gray-700 mb-1">
                {mode === 'phone'
                  ? t('signIn.phoneLabel', { defaultValue: 'Phone number' })
                  : t('signIn.emailLabel', { defaultValue: 'Email' })}
              </label>
              {mode === 'phone' ? (
                <PhoneInput
                  id="identifier"
                  value={identifier}
                  onChange={setIdentifier}
                  placeholder={t('signIn.phonePlaceholder', { defaultValue: '612 345 678' })}
                  autoFocus
                  required
                />
              ) : (
                <input
                  id="identifier"
                  type="email"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  placeholder={t('signIn.emailPlaceholder', { defaultValue: 'you@example.com' })}
                  autoFocus
                  required
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
                />
              )}
            </div>
            {/* Signing up with a phone still requires an email: Meta caps
                WhatsApp per recipient and silently drops the overflow, so an
                account with no email has no route to delivery once throttled.
                This address is verified separately — the OTP below goes to
                the phone — so it lands pending until its own confirmation. */}
            {mode === 'phone' && (
              <div>
                <label htmlFor="backup-email" className="mb-1 block text-sm font-medium text-gray-700">
                  {t('signIn.emailLabel', { defaultValue: 'Email address' })}
                </label>
                <input
                  id="backup-email"
                  type="email"
                  value={backupEmail}
                  onChange={(e) => setBackupEmail(e.target.value)}
                  placeholder={t('signIn.emailPlaceholder', { defaultValue: 'you@example.com' })}
                  required
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
                />
                <p className="mt-1 text-xs text-gray-500">
                  {t('signIn.emailBackupHint', {
                    defaultValue: "For your account, and as a backup if WhatsApp can't deliver.",
                  })}
                </p>
              </div>
            )}
            {identifier.trim() && !isEmailIdentifier && <WhatsAppSenderHint />}
            <TurnstileWidget onToken={handleTurnstileToken} />
            {existingAccountMethods && existingAccountMethods.length > 0 && !fromLandingFlow && (
              <div
                role="alert"
                className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-900"
              >
                <p className="font-medium">
                  {t('signIn.existingAccountTitle', {
                    defaultValue: 'This account was created with a social login',
                  })}
                </p>
                <p className="mt-1 text-xs text-amber-800">
                  {t('signIn.existingAccountBody', {
                    defaultValue:
                      'Sign in with the same provider to keep all your data in one account.',
                  })}
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {existingAccountMethods.includes('google') && (
                    <button
                      type="button"
                      onClick={signInWithGoogle}
                      className="rounded-md bg-white px-3 py-1.5 text-xs font-semibold text-gray-800 border border-gray-300 hover:bg-gray-50"
                    >
                      {t('signIn.continueWithGoogle', { defaultValue: 'Continue with Google' })}
                    </button>
                  )}
                  {existingAccountMethods.includes('github') && (
                    <button
                      type="button"
                      onClick={signInWithGitHub}
                      className="rounded-md bg-white px-3 py-1.5 text-xs font-semibold text-gray-800 border border-gray-300 hover:bg-gray-50"
                    >
                      {t('signIn.continueWithGitHub', { defaultValue: 'Continue with GitHub' })}
                    </button>
                  )}
                </div>
              </div>
            )}
            {existingAccountMethods && existingAccountMethods.length > 0 && fromLandingFlow && (
              // Landing flow is intentionally single-path. We don't expose
              // the "Continue with Google/GitHub" buttons here, but we
              // can't silently fail either — the user has to be told why
              // the OTP isn't being sent. Point them at the regular
              // sign-in page where the alternative providers are shown.
              <div
                role="alert"
                className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-900"
              >
                <p className="font-medium">
                  {t('signIn.existingAccountTitle', {
                    defaultValue: 'This account was created with a social login',
                  })}
                </p>
                <p className="mt-1 text-xs text-amber-800">
                  {t('signIn.existingAccountLandingBody', {
                    defaultValue:
                      'Please use the regular sign-in page to continue with the original provider.',
                  })}
                </p>
                <Link
                  to="/signin"
                  className="mt-3 inline-block rounded-md bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-700 transition-colors"
                >
                  {t('signIn.goToSignIn', { defaultValue: 'Go to sign in' })}
                </Link>
              </div>
            )}
            {error && <p className="text-sm text-alert-500">{error}</p>}
            <button
              type="submit"
              disabled={submitting || !identifier.trim() || (turnstileConfigured && !turnstileToken)}
              className="w-full rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50 transition-colors"
            >
              {submitting
                ? t('signIn.sending', { defaultValue: 'Sending…' })
                : t('signIn.sendCode', { defaultValue: 'Send code' })}
            </button>
            {!fromLandingFlow && (
              <SocialAuthButtons onGoogle={signInWithGoogle} onGitHub={signInWithGitHub} disabled={submitting} />
            )}
          </form>
        ) : (
          <form onSubmit={handleConfirmCode} className="space-y-4">
            <div>
              <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-1">
                {t('code.label', { defaultValue: '6-digit code' })}
              </label>
              <input
                id="code"
                type="text"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                autoFocus
                required
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-center text-lg tracking-[0.5em] focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
              />
            </div>
            {channel === 'whatsapp' && <WhatsAppSenderHint />}
            {error && <p className="text-sm text-alert-500">{error}</p>}
            <button
              type="submit"
              disabled={submitting || code.length !== 6}
              className="w-full rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50 transition-colors"
            >
              {submitting
                ? t('code.verifying', { defaultValue: 'Verifying…' })
                : t('code.confirm', { defaultValue: 'Sign in' })}
            </button>
            <button
              type="button"
              onClick={() => { setStep('identifier'); setCode(''); setError(''); }}
              className="w-full text-sm text-gray-500 hover:text-gray-700"
            >
              {t('code.back', { defaultValue: '← Change identifier' })}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
